import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddDeliveryRegionOptions,
  AddDeliveryRegionResponse,
  BulkCreatePickupLocationApplicationErrors,
  BulkCreatePickupLocationResponse,
  BulkDeletePickupLocationResponse,
  BulkUpdatePickupLocationResponse,
  CreatePickupLocationApplicationErrors,
  PickupLocation,
  PickupLocationQuery,
  PickupLocationsQueryBuilder,
  QueryPickupLocationResponse,
  RemoveDeliveryRegionOptions,
  RemoveDeliveryRegionResponse,
  UpdatePickupLocation,
  addDeliveryRegion as universalAddDeliveryRegion,
  bulkCreatePickupLocation as universalBulkCreatePickupLocation,
  bulkDeletePickupLocation as universalBulkDeletePickupLocation,
  bulkUpdatePickupLocation as universalBulkUpdatePickupLocation,
  createPickupLocation as universalCreatePickupLocation,
  deletePickupLocation as universalDeletePickupLocation,
  getPickupLocation as universalGetPickupLocation,
  queryPickupLocation as universalQueryPickupLocation,
  removeDeliveryRegion as universalRemoveDeliveryRegion,
  typedQueryPickupLocation as universalTypedQueryPickupLocation,
  updatePickupLocation as universalUpdatePickupLocation,
} from './ecom-v1-pickup-location-pickup-locations.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createPickupLocation(
  httpClient: HttpClient
): CreatePickupLocationSignature {
  return (pickupLocation: NonNullablePaths<PickupLocation, `address`, 2>) =>
    universalCreatePickupLocation(
      pickupLocation,
      // @ts-ignore
      { httpClient }
    );
}

interface CreatePickupLocationSignature {
  /**
   * Creates a new PickupLocation
   * @param - PickupLocation to be created
   * @returns The created PickupLocation
   */
  (pickupLocation: NonNullablePaths<PickupLocation, `address`, 2>): Promise<
    NonNullablePaths<
      PickupLocation,
      | `address.streetAddress.number`
      | `address.streetAddress.name`
      | `rates`
      | `rates.${number}.amount`
      | `rates.${number}.multiplyByQuantity`
      | `deliveryRegionIds`,
      4
    > & {
      __applicationErrorsType?: CreatePickupLocationApplicationErrors;
    }
  >;
}

export function getPickupLocation(
  httpClient: HttpClient
): GetPickupLocationSignature {
  return (pickupLocationId: string) =>
    universalGetPickupLocation(
      pickupLocationId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPickupLocationSignature {
  /**
   * Get a PickupLocation by id
   * @param - Id of the PickupLocation to retrieve
   * @returns The retrieved PickupLocation
   */
  (pickupLocationId: string): Promise<
    NonNullablePaths<
      PickupLocation,
      | `address.streetAddress.number`
      | `address.streetAddress.name`
      | `rates`
      | `rates.${number}.amount`
      | `rates.${number}.multiplyByQuantity`
      | `deliveryRegionIds`,
      4
    >
  >;
}

export function updatePickupLocation(
  httpClient: HttpClient
): UpdatePickupLocationSignature {
  return (
    _id: string,
    pickupLocation: NonNullablePaths<UpdatePickupLocation, `revision`, 2>
  ) =>
    universalUpdatePickupLocation(
      _id,
      pickupLocation,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePickupLocationSignature {
  /**
   * Update a PickupLocation
   * Delivery regions cannot be updated using this method, use AddDeliveryRegion and RemoveDeliveryRegion instead.
   * @param - PickupLocation ID
   * @returns The updated PickupLocation
   */
  (
    _id: string,
    pickupLocation: NonNullablePaths<UpdatePickupLocation, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      PickupLocation,
      | `address.streetAddress.number`
      | `address.streetAddress.name`
      | `rates`
      | `rates.${number}.amount`
      | `rates.${number}.multiplyByQuantity`
      | `deliveryRegionIds`,
      4
    >
  >;
}

export function deletePickupLocation(
  httpClient: HttpClient
): DeletePickupLocationSignature {
  return (pickupLocationId: string) =>
    universalDeletePickupLocation(
      pickupLocationId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeletePickupLocationSignature {
  /**
   * Delete a PickupLocation
   * @param - Id of the PickupLocation to delete
   */
  (pickupLocationId: string): Promise<void>;
}

export function queryPickupLocation(
  httpClient: HttpClient
): QueryPickupLocationSignature {
  return () =>
    universalQueryPickupLocation(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPickupLocationSignature {
  /**
   * Query PickupLocations using [WQL - Wix Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
   */
  (): PickupLocationsQueryBuilder;
}

export function typedQueryPickupLocation(
  httpClient: HttpClient
): TypedQueryPickupLocationSignature {
  return (query: PickupLocationQuery) =>
    universalTypedQueryPickupLocation(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryPickupLocationSignature {
  /** */
  (query: PickupLocationQuery): Promise<
    NonNullablePaths<
      QueryPickupLocationResponse,
      | `pickupLocations`
      | `pickupLocations.${number}.address.streetAddress.number`
      | `pickupLocations.${number}.address.streetAddress.name`,
      6
    >
  >;
}

export function addDeliveryRegion(
  httpClient: HttpClient
): AddDeliveryRegionSignature {
  return (
    pickupLocationId: string,
    deliveryRegionId: string,
    options: NonNullablePaths<AddDeliveryRegionOptions, `revision`, 2>
  ) =>
    universalAddDeliveryRegion(
      pickupLocationId,
      deliveryRegionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddDeliveryRegionSignature {
  /**
   * Add a DeliveryRegion to a PickupLocation
   * @param - Id of the PickupLocation to add to the delivery region
   * @param - Id of the DeliveryRegion to add the PickupLocation to
   */
  (
    pickupLocationId: string,
    deliveryRegionId: string,
    options: NonNullablePaths<AddDeliveryRegionOptions, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      AddDeliveryRegionResponse,
      | `pickupLocation.address.streetAddress.number`
      | `pickupLocation.address.streetAddress.name`
      | `pickupLocation.rates`
      | `pickupLocation.rates.${number}.amount`
      | `pickupLocation.rates.${number}.multiplyByQuantity`
      | `pickupLocation.deliveryRegionIds`,
      5
    >
  >;
}

export function removeDeliveryRegion(
  httpClient: HttpClient
): RemoveDeliveryRegionSignature {
  return (
    pickupLocationId: string,
    deliveryRegionId: string,
    options: NonNullablePaths<RemoveDeliveryRegionOptions, `revision`, 2>
  ) =>
    universalRemoveDeliveryRegion(
      pickupLocationId,
      deliveryRegionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveDeliveryRegionSignature {
  /**
   * Remove a DeliveryRegion from a PickupLocation
   * @param - Id of the PickupLocation to add to the delivery region
   * @param - Id of the DeliveryRegion to add the PickupLocation to
   */
  (
    pickupLocationId: string,
    deliveryRegionId: string,
    options: NonNullablePaths<RemoveDeliveryRegionOptions, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      RemoveDeliveryRegionResponse,
      | `pickupLocation.address.streetAddress.number`
      | `pickupLocation.address.streetAddress.name`
      | `pickupLocation.rates`
      | `pickupLocation.rates.${number}.amount`
      | `pickupLocation.rates.${number}.multiplyByQuantity`
      | `pickupLocation.deliveryRegionIds`,
      5
    >
  >;
}

export function bulkCreatePickupLocation(
  httpClient: HttpClient
): BulkCreatePickupLocationSignature {
  return (pickupLocations: NonNullablePaths<PickupLocation, `address`, 2>[]) =>
    universalBulkCreatePickupLocation(
      pickupLocations,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreatePickupLocationSignature {
  /**
   * Bulk Create for new PickupLocation
   */
  (pickupLocations: NonNullablePaths<PickupLocation, `address`, 2>[]): Promise<
    NonNullablePaths<
      BulkCreatePickupLocationResponse,
      | `pickupLocations`
      | `pickupLocations.${number}.address.streetAddress.number`
      | `pickupLocations.${number}.address.streetAddress.name`
      | `errors`
      | `errors.${number}._id`
      | `errors.${number}.error.code`
      | `errors.${number}.error.description`,
      6
    > & {
      __applicationErrorsType?: BulkCreatePickupLocationApplicationErrors;
    }
  >;
}

export function bulkUpdatePickupLocation(
  httpClient: HttpClient
): BulkUpdatePickupLocationSignature {
  return (
    pickupLocations: NonNullablePaths<PickupLocation, `_id` | `revision`, 2>[]
  ) =>
    universalBulkUpdatePickupLocation(
      pickupLocations,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdatePickupLocationSignature {
  /**
   * Update a PickupLocation
   */
  (
    pickupLocations: NonNullablePaths<PickupLocation, `_id` | `revision`, 2>[]
  ): Promise<
    NonNullablePaths<
      BulkUpdatePickupLocationResponse,
      | `pickupLocations`
      | `pickupLocations.${number}.address.streetAddress.number`
      | `pickupLocations.${number}.address.streetAddress.name`
      | `errors`
      | `errors.${number}._id`
      | `errors.${number}.error.code`
      | `errors.${number}.error.description`,
      6
    >
  >;
}

export function bulkDeletePickupLocation(
  httpClient: HttpClient
): BulkDeletePickupLocationSignature {
  return (pickupLocationIds: string[]) =>
    universalBulkDeletePickupLocation(
      pickupLocationIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeletePickupLocationSignature {
  /**
   * Delete a PickupLocation
   */
  (pickupLocationIds: string[]): Promise<
    NonNullablePaths<
      BulkDeletePickupLocationResponse,
      | `errors`
      | `errors.${number}._id`
      | `errors.${number}.error.code`
      | `errors.${number}.error.description`,
      5
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AddDeliveryRegionOptions,
  AddDeliveryRegionRequest,
  AddDeliveryRegionResponse,
  Address,
  AddressStreetOneOf,
  ApplicationError,
  BulkCreatePickupLocationRequest,
  BulkCreatePickupLocationResponse,
  BulkDeletePickupLocationRequest,
  BulkDeletePickupLocationResponse,
  BulkUpdatePickupLocationRequest,
  BulkUpdatePickupLocationResponse,
  Condition,
  ConditionType,
  ConditionalRates,
  CreatePickupLocationRequest,
  CreatePickupLocationResponse,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  DeletePickupLocationRequest,
  DeletePickupLocationResponse,
  DiffmatokyPayload,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ErrorInformation,
  GetPickupLocationRequest,
  GetPickupLocationResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  LogicalOperator,
  MessageEnvelope,
  Paging,
  PickupLocation,
  PickupLocationError,
  PickupLocationQuerySpec,
  PickupLocationsQueryBuilder,
  PickupLocationsQueryResult,
  QueryPickupLocationRequest,
  QueryPickupLocationResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RemoveDeliveryRegionOptions,
  RemoveDeliveryRegionRequest,
  RemoveDeliveryRegionResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  StreetAddress,
  UpdatePickupLocation,
  UpdatePickupLocationRequest,
  UpdatePickupLocationResponse,
  WebhookIdentityType,
  utils,
} from './ecom-v1-pickup-location-pickup-locations.universal.js';
